// store.jsx — CardAIResultsContext (con lógica TTS integrada)
import { createContext } from "preact";
import { useContext, useMemo, useState, useCallback, useRef, useEffect } from "preact/hooks";
import { useQuotationByIdMultiRole } from "../store";

export const CardAIResultsContext = createContext(null);

export function useCardAIResults() {
  const ctx = useContext(CardAIResultsContext);
  if (!ctx) throw new Error("useCardAIResults must be used within CardAIResultsProvider");
  return ctx;
}

export function CardAIResultsProvider({ children }) {
  const { quotation, quotationId, role, isLoading, error } = useQuotationByIdMultiRole();

  // -------- TTS State --------
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [ttsError, setTtsError] = useState(null);
  const [ttsAudioUrl, setTtsAudioUrl] = useState(null);
  const ttsAudioRef = useRef(null);

  // Derivados expuestos al componente (directamente del store padre)
  const statusName = String(quotation?.status?.name || "").toLowerCase();
  const isCreated = statusName === "created";
  const isInAIPhase = statusName === "ai-review";
  
  // Ordenar aiReviews por fecha de creación descendente
  const aiReviews = useMemo(() => {
    if (!Array.isArray(quotation?.aiReviews)) return [];
    return [...quotation.aiReviews].sort((a, b) =>
      new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0)
    );
  }, [quotation?.aiReviews]);

  const lastReview = useMemo(() => (aiReviews.length ? aiReviews[0] : null), [aiReviews]);

  // -------- TTS Functions --------
  const generateTTSAudio = useCallback(async (text) => {
    if (ttsLoading || !text) return;

    setTtsLoading(true);
    setTtsError(null);

    try {
      // Limpiar markdown del texto
      const cleanText = text.replace(/[#*`_\\[\]]/g, '');
      const url = `/${role}/api/tts/generate`;

      // console.log('[TTS] Iniciando generación de audio');
      // console.log('[TTS] URL:', url);
      // console.log('[TTS] Role:', role);
      // console.log('[TTS] Text length:', cleanText.length);

      // Usar native fetch para llamar al backend
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ text: cleanText }),
      });

      // console.log('[TTS] Response status:', response.status);
      // console.log('[TTS] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error al generar audio' }));
        console.error('[TTS] Error response:', errorData);
        throw new Error(errorData.message || 'Error al generar audio');
      }

      // Obtener blob del response
      const audioBlob = await response.blob();
      // console.log('[TTS] Blob received:', audioBlob.type, audioBlob.size, 'bytes');

      // Usar blob URL (más eficiente que data URL para audio)
      const blobUrl = URL.createObjectURL(audioBlob);
      // console.log('[TTS] Blob URL created:', blobUrl);
      setTtsAudioUrl(blobUrl);

      // Limpiar audio anterior
      if (ttsAudioRef.current) {
        // console.log('[TTS] Cleaning up previous audio');
        ttsAudioRef.current.pause();
        if (ttsAudioRef.current.src && ttsAudioRef.current.src.startsWith('blob:')) {
          URL.revokeObjectURL(ttsAudioRef.current.src);
        }
      }

      // Crear y reproducir nuevo audio
      const audio = new Audio();
      audio.src = blobUrl;
      audio.preload = 'auto';
      audio.volume = 1.0;
      ttsAudioRef.current = audio;

      // Event handlers (keep only essential error logging)
      // audio.onloadstart = () => console.log('[TTS] 🔵 loadstart');
      // audio.onloadedmetadata = () => {
      //   console.log('[TTS] 🔵 loadedmetadata - Duration:', audio.duration, 'seconds');
      // };
      // audio.onloadeddata = () => console.log('[TTS] 🔵 loadeddata');
      // audio.oncanplay = () => console.log('[TTS] 🔵 canplay - ready to play');
      
      // Esperar a que el audio esté COMPLETAMENTE cargado antes de reproducir
      audio.oncanplaythrough = async () => {
        // console.log('[TTS] 🔵 canplaythrough - fully loaded, attempting play');
        try {
          await audio.play();
          setTtsPlaying(true);
          // console.log('[TTS] ✅ play() successful');
        } catch (err) {
          console.error('[TTS] Play failed:', err);
          setTtsError('Browser blocked audio: ' + err.message);
          setTtsPlaying(false);
        }
      };

      // audio.onplay = () => console.log('[TTS] 🟢 play event fired');
      // audio.onplaying = () => console.log('[TTS] 🟢 playing');
      // audio.onpause = () => console.log('[TTS] 🟡 pause');
      // audio.ontimeupdate = () => {};
      
      audio.onended = () => {
        // console.log('[TTS] ✅ ended');
        setTtsPlaying(false);
      };
      
      audio.onerror = (e) => {
        console.error('[TTS] Audio error:', audio.error?.code, audio.error?.message);
        setTtsError('Error al reproducir el audio');
        setTtsPlaying(false);
      };
      
      // audio.onstalled = () => console.log('[TTS] 🔴 stalled');
      // audio.onsuspend = () => console.log('[TTS] 🔴 suspend');

      // console.log('[TTS] Audio element created, waiting for canplaythrough...');
    } catch (err) {
      console.error('[TTS] Generation error:', err);
      setTtsError(err?.message || 'Error al generar audio');
    } finally {
      setTtsLoading(false);
    }
  }, [role, ttsLoading]);

  const handleTTSPlayPause = useCallback((text) => {
    if (!ttsAudioUrl) {
      generateTTSAudio(text);
      return;
    }

    if (ttsAudioRef.current) {
      if (ttsPlaying) {
        ttsAudioRef.current.pause();
        setTtsPlaying(false);
      } else {
        ttsAudioRef.current.play();
        setTtsPlaying(true);
      }
    }
  }, [ttsAudioUrl, ttsPlaying, generateTTSAudio]);

  const cleanupTTS = useCallback(() => {
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      if (ttsAudioUrl) {
        URL.revokeObjectURL(ttsAudioUrl);
      }
    }
    setTtsAudioUrl(null);
    setTtsPlaying(false);
    setTtsError(null);
  }, [ttsAudioUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (ttsAudioRef.current) {
        ttsAudioRef.current.pause();
      }
      if (ttsAudioUrl) {
        URL.revokeObjectURL(ttsAudioUrl);
      }
    };
  }, [ttsAudioUrl]);

  const value = useMemo(
    () => ({
      quotation,
      quotationId,
      role,
      isLoading,
      error,
      statusName,
      isCreated,
      isInAIPhase,
      aiReviews,
      lastReview,
      // TTS
      ttsLoading,
      ttsPlaying,
      ttsError,
      handleTTSPlayPause,
      cleanupTTS,
    }),
    [
      quotation,
      quotationId,
      role,
      isLoading,
      error,
      statusName,
      isCreated,
      isInAIPhase,
      aiReviews,
      lastReview,
      ttsLoading,
      ttsPlaying,
      ttsError,
      handleTTSPlayPause,
      cleanupTTS,
    ]
  );

  return (
    <CardAIResultsContext.Provider value={value}>
      {children}
    </CardAIResultsContext.Provider>
  );
}
